import React, { useState } from 'react';
import { FiArrowUpRight, FiMonitor, FiTarget, FiFileText, FiZap, FiSend } from 'react-icons/fi';
import './Products.css';
import ApplicationForm from './ApplicationForm';
import { triggerApplyModal } from '../App';

const Products = () => {
  const [showApplicationForm, setShowApplicationForm] = useState(false);

  const statistics = [
    { number: '120+', label: 'Tech Projects Delivered' },
    { number: '40+', label: 'Enterprise & Business Clients' },
    { number: '99.8%', label: 'Project On-Time Rate' },
    { number: '24/7', label: 'Dedicated Support & Maintenance' }
  ];

  const productsList = [
    {
      id: 'custom-web-suite',
      type: 'Web Engineering',
      badge: 'Core Offering',
      title: 'Enterprise Web & Portal Suite',
      subtitle: 'High-Speed Web Apps, Client Dashboards & Portals',
      description: 'Custom React, Next.js, and Node.js web applications engineered for speed, security, and responsive performance across all screen sizes.',
      features: [
        'Fintech & E-commerce Customer Portals',
        'Admin & Real-Time Analytics Dashboards',
        'Progressive Web App (PWA) Capabilities',
        'Sub-second page load speeds & SEO'
      ]
    },
    {
      id: 'mobile-app-suite',
      type: 'Mobile Engineering',
      badge: 'iOS & Android',
      title: 'Mobile Application Suite',
      subtitle: 'Cross-Platform & Native iOS/Android Apps',
      description: 'Feature-packed iOS and Android apps built with Flutter & React Native. Includes biometric security, OTP verification, push alerts, and payment gateways.',
      features: [
        'Cross-platform Flutter & React Native codebase',
        'Biometric & Mobile OTP Login',
        'Integrated Payment Gateways (Razorpay, Stripe)',
        'App Store & Play Store Deployment'
      ]
    },
    {
      id: 'fintech-software-platform',
      type: 'Fintech Software',
      badge: 'Fintech Solutions',
      title: 'Custom Fintech & Payment Software Engine',
      subtitle: 'Loan Origination, e-KYC & NBFC Portals',
      description: 'Tailored financial software platforms for NBFCs, lending institutions, and fintech startups to automate loan origination, e-KYC checks, and payment collections.',
      features: [
        'Automated Aadhaar & PAN e-KYC Workflows',
        'Integrated Credit Scoring & CIBIL API Engine',
        'Automated Loan Disbursal & Repayment Ledger',
        'Bank-Grade 256-bit SSL Security Standards'
      ]
    },
    {
      id: 'saas-cloud-platform',
      type: 'SaaS & Cloud',
      badge: 'Cloud Architecture',
      title: 'Custom SaaS & Cloud Microservices Platform',
      subtitle: 'Scalable Backends & REST/GraphQL API Suites',
      description: 'Enterprise cloud architectures engineered on AWS and GCP with microservices, auto-scaling, Docker containers, and high-throughput REST/GraphQL APIs.',
      features: [
        'Multi-tenant SaaS Architecture',
        'High-speed API Latency (<50ms)',
        'Auto-scaling Server Infrastructure',
        '24/7 Monitoring & DevOps Pipeline'
      ]
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Project Brief & Scope',
      description: 'Share your requirements and technical goals for a free technical consultation.',
      icon: <FiTarget />
    },
    {
      step: 2,
      title: 'UI/UX & Architecture Plan',
      description: 'Receive clickable wireframes, database schemas, and milestone timelines.',
      icon: <FiFileText />
    },
    {
      step: 3,
      title: 'Agile Sprint Engineering',
      description: 'Our senior developers build your application in high-speed, 2-week sprints.',
      icon: <FiZap />
    },
    {
      step: 4,
      title: 'QA & Cloud Launch',
      description: 'Rigorous security testing and smooth production deployment with code ownership.',
      icon: <FiSend />
    }
  ];

  return (
    <div className="products-page">
      {/* Hero Section */}
      <section className="hero-section products-hero-section">
        <div className="hero-overlay products-hero-overlay">
          <div className="container products-hero-container">
            <h1 className="hero-title products-hero-title">
              Software Solutions & Tech Services
              <span className="product-subtitle">Engineering Next-Gen Digital Products For Modern Businesses</span>
            </h1>
            <p className="hero-subtitle products-hero-subtitle">
              From web applications and native mobile apps to custom fintech platforms and cloud microservices, <strong>Kubersetu Infotech</strong> builds scalable technology.
            </p>
            <div className="hero-cta-group products-hero-cta-group">
              <button 
                className="btn-hero"
                onClick={() => triggerApplyModal('Custom Web Application Development')}
              >
                Request Free Quote <FiArrowUpRight />
              </button>
              <a 
                href="/services" 
                className="btn-hero-secondary"
              >
                Explore All Services <FiMonitor />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {statistics.map((stat, index) => (
              <div key={index} className="stat-card">
                <h3 className="stat-number">{stat.number}</h3>
                <p className="stat-label">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products & Tech Services Grid */}
      <section className="products-section">
        <div className="container">
          <div className="section-header">
            <span className="kubersetu-badge">SOFTWARE & TECH OFFERINGS</span>
            <h2 className="section-title">Custom Software Engineering & Tech Products</h2>
            <p className="section-subtitle">
              We specialize in custom web development, mobile apps, fintech software platforms, and cloud solutions.
            </p>
          </div>

          <div className="products-grid">
            {productsList.map((prod) => (
              <div key={prod.id} className="product-card">
                <div className="product-content">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: '800', color: 'var(--color-amber)', letterSpacing: '0.05em' }}>
                      {prod.badge}
                    </span>
                    <span style={{ background: 'rgba(255,255,255,0.08)', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem', color: '#fff' }}>
                      {prod.type}
                    </span>
                  </div>

                  <h3 className="product-name">{prod.title}</h3>
                  <p style={{ color: '#FBBF24', fontSize: '0.95rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                    {prod.subtitle}
                  </p>
                  <p className="product-description">{prod.description}</p>

                  <div className="detail-section">
                    <h4>Key Capabilities:</h4>
                    <ul>
                      {prod.features.map((feat, idx) => (
                        <li key={idx}>{feat}</li>
                      ))}
                    </ul>
                  </div>

                  <button 
                    className="btn-apply"
                    onClick={() => triggerApplyModal(prod.title)}
                  >
                    Request Proposal <FiArrowUpRight />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Seamless 4-Step Engineering Process</h2>
          </div>
          
          <div className="process-steps">
            {howItWorks.map((step) => (
              <div key={step.step} className="process-step">
                <div className="step-icon">{step.icon}</div>
                <div className="step-number">Step {step.step}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Partner with Kubersetu Infotech?</h2>
            <p className="cta-subtitle">
              Whether you need high-performance web development, custom iOS/Android mobile apps, or enterprise software engineering, we are here to power your digital growth.
            </p>
            <div className="cta-buttons">
              <button 
                className="btn-cta-primary"
                onClick={() => triggerApplyModal('Custom Web Application Development')}
              >
                Consult Tech Engineering Team <FiArrowUpRight />
              </button>
            </div>
          </div>
        </div>
      </section>

      {showApplicationForm && (
        <ApplicationForm onClose={() => setShowApplicationForm(false)} />
      )}
    </div>
  );
};

export default Products;
